import {set} from '@servicenow/ui-utils';

export default class QueryContext {
	constructor(contextArguments) {
		const {
			initialPayloadActionType,
			additionalDataActionType,
			successActionType,
			dispatch,
			actionMetaData = {},
			span
		} = contextArguments;
		this.initialPayloadActionType = initialPayloadActionType;
		this.additionalDataActionType = additionalDataActionType;
		this.successActionType = successActionType;
		this.dispatchFunction = dispatch;
		this.actionMetadata = actionMetaData;
		this.activeSpan = span;
		this.resetContext();
	}

	dataReceived(dataReceived, responseHeaders = null) {
		if (dataReceived === null || dataReceived === undefined || dataReceived.data === undefined) {
			if (this.initialPayloadEmpty) this.setFirstDataReceived({data: {}});
			return;
		}

		//Set initial payload data
		if (this.initialPayloadEmpty) {
			this.setFirstDataReceived(dataReceived);
			this.dispatchInitialPayloadAction(responseHeaders);
		} else {
			this.updateDataReceived(dataReceived);
			this.dispatchAdditionalDataAction(dataReceived, responseHeaders);
		}
	}

	setFirstDataReceived(data) {
		this.initialPayloadEmpty = false;
		this.finalPayload = data;
	}

	updateDataReceived(updatePayload) {
		//Merge new data
		if (Array.isArray(updatePayload.path))
			set(this.finalPayload.data, updatePayload.path, updatePayload.data);

		//Merge any new errors
		const receivedErrors = updatePayload.errors;
		if (Array.isArray(receivedErrors) && receivedErrors.length) {
			if (!Array.isArray(this.finalPayload.errors)) this.finalPayload.errors = [];

			this.finalPayload.errors = this.finalPayload.errors.concat(receivedErrors);
		}
	}

	successPayloadReceived(payload, responseHeaders = null) {
		//If we've already received data then we can ignore this payload and just dispatch the
		//success action, otherwise we set the payload, and if they have designated a dataReceivedActionType
		//we dispatch that also
		if (this.initialPayloadEmpty) {
			this.setFirstDataReceived(payload);
			this.dispatchInitialPayloadAction(responseHeaders, false);
		}

		this.dispatchSuccessAction(responseHeaders);
	}

	dispatchInitialPayloadAction(responseHeaders, shouldClone = true) {
		//Don't dispatch if we have a PersistedQueryException! Seems kind of arbitrary here, not sure where
		//else to put this check
		const {errors = []} = this.finalPayload;
		if (errors.some(({errorType}) => errorType === 'PersistedQueryNotFound')) return;

		if (this.dispatchFunction && this.initialPayloadActionType) {
			this.dispatchFunction(
				this.initialPayloadActionType,
				//Since this is just JSON from the graphql response, we can do a stringify/parse to clone it
				shouldClone ? JSON.parse(JSON.stringify(this.finalPayload)) : this.finalPayload,
				{
					...this.actionMetadata,
					responseHeaders
				}
			);
		}
	}

	dispatchAdditionalDataAction(dataReceived, responseHeaders) {
		if (this.dispatchFunction && this.additionalDataActionType) {
			this.dispatchFunction(this.additionalDataActionType, dataReceived, {
				...this.actionMetadata,
				responseHeaders
			});
		}
	}

	dispatchSuccessAction(responseHeaders) {
		if (this.dispatchFunction && this.successActionType) {
			this.dispatchFunction(this.successActionType, this.finalPayload, {
				...this.actionMetadata,
				...(this.activeSpan && {span: this.activeSpan}),
				responseHeaders
			});
		}
	}

	get response() {
		return this.finalPayload;
	}

	isStreamingRequest() {
		return Boolean(this.initialPayloadActionType || this.additionalDataActionType);
	}

	wasStreamingResponse(headers) {
		return Boolean(
			headers?.['content-type']?.includes('multipart/mixed') && this.isStreamingRequest()
		);
	}

	clearStreaming() {
		this.initialPayloadActionType = undefined;
		this.additionalDataActionType = undefined;
	}

	resetContext() {
		this.finalPayload = {data: {}};
		this.initialPayloadEmpty = true;
	}

	getActiveSpan() {
		return this.activeSpan;
	}

	//We dont want this to serialize to anything in case it is included in requests
	toJSON() {
		return null;
	}
}
